import React, { useEffect, useCallback, useMemo } from "react";
import { createContainer } from "unstated-next";
import { useAuthState } from "./useAuthState";
import { useErrorHandlerWithoutReAuth } from "@/modules/app/useErrorHandler";
import useRepository from "./useRepository";
import {
  AuthenticationResponse,
  AuthSystemType,
  AuthenticateStatus
} from "@travello/grpc-api/js/authenticate_pb";
import { useUpdateEffect } from "react-use";

import { useLazyQuery } from "@apollo/client";

import { ME_QUERY } from "./queries";
import { MeQuery, MeQuery_me_Me } from "./__generated__/MeQuery";
import useTracking from "./useTracking";
import useRouter from "@/utils/useRouter";
import capitalize from "lodash/capitalize";
import startsWith from "lodash/startsWith";
import cache from "@/pages/cache";

const AUTHENTICATE_STATUS = Object.keys(AuthenticateStatus);

const useAuth_ = () => {
  const [
    { token, serviceMetaData, restHeaders },
    { setToken }
  ] = useAuthState();

  const { history, location } = useRouter();

  const {
    loginWithEmail$,
    loginWithToken$,
    resetPassword$,
    signupWithEmail$,
    loginAsGuest$
  } = useRepository();

  const {
    handleGrpcError: handleError,
    handleGenericError
  } = useErrorHandlerWithoutReAuth();

  const loginAsGuest = useCallback(async () => {
    try {
      await cache.reset();
      const { token } = await loginAsGuest$();
      return setToken(token);
    } catch (e) {
      handleError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token || token === window.guestToken) {
      // refresh guestToken on client
      loginAsGuest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [fetchMe, { data: meData, refetch: refetchMe }] = useLazyQuery<MeQuery>(
    ME_QUERY,
    {
      fetchPolicy: "no-cache",
      partialRefetch: true,
      returnPartialData: true
    }
  );

  const {
    user,
    guestOrUserId,
    userId,
    otherSettings,
    isGuest
  } = useMemo(() => {
    const me = meData?.me;
    const user = (me as MeQuery_me_Me | undefined)?.user;
    return {
      isGuest: !!token && meData?.me.__typename === "GuestMe",
      guestOrUserId: me?.id,
      me,
      userId: user?.id,
      user,
      otherSettings: meData?.me
    };
  }, [token, meData]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (refetchMe) {
      refetchMe().catch(() => loginAsGuest());
    } else {
      fetchMe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useTracking({
    userId: guestOrUserId, // this should track guest user and normal user
    gender: user?.gender,
    preferredCurrency: otherSettings?.preferredCurrency
  });

  useUpdateEffect(() => {
    if (!otherSettings) {
      return;
    }
    if (otherSettings.__typename === "Me") {
      if (
        !otherSettings.privacySettings ||
        !otherSettings.privacySettings.email
      ) {
        history.push("/me/edit/privacy", {
          background: location.state?.background || location
        });
      }
    }
  }, [otherSettings]);

  const handleAuthResponse = useCallback(
    async ({ responseStatus, token }: AuthenticationResponse.AsObject) => {
      if (token) {
        history.push(location.state?.background || "/");
        return setToken(token);
      } else if (
        [
          AuthenticateStatus.ERROR_INVALID_EMAIL,
          AuthenticateStatus.ERROR_INVALID_PASSWORD,
          AuthenticateStatus.ERROR_INCORRECT_PASSWORD,
          AuthenticateStatus.ERROR_INVALID_USERNAME
        ].includes(responseStatus)
      ) {
        return handleGenericError(new Error("Incorrect username or password"));
      } else if (startsWith(AUTHENTICATE_STATUS[responseStatus], "ERROR_")) {
        return handleGenericError(
          new Error(
            capitalize(
              AUTHENTICATE_STATUS[responseStatus]
                .replace("ERROR_", "")
                .replace(/_/g, " ")
            )
          )
        );
      }
    },
    [setToken, history, location, handleGenericError]
  );

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await loginWithEmail$({
          username: email,
          password
        });
        return await handleAuthResponse(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleAuthResponse, handleError, loginWithEmail$]
  );

  const loginWithToken = useCallback(
    async (token: string, loginType: AuthSystemType) => {
      try {
        const response = await loginWithToken$({ token, system: loginType });
        return await handleAuthResponse(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleAuthResponse, handleError, loginWithToken$]
  );

  const signupWithEmail = useCallback(
    async (userName: string, email: string, password: string) => {
      try {
        const response = await signupWithEmail$({
          userName,
          email,
          password
        });
        return await handleAuthResponse(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleAuthResponse, handleError, signupWithEmail$]
  );

  const resetPassword = useCallback(
    async (email: string) => {
      try {
        await resetPassword$({ email });
        return true;
      } catch (e) {
        handleError(e);
      }
      return false;
    },
    [handleError, resetPassword$]
  );

  const state = {
    user,
    userId,
    token,
    otherSettings,
    serviceMetaData,
    restHeaders,
    isGuest,
    isLoading: !token
  };

  const actions = {
    refetchMe,
    signupWithEmail,
    loginWithEmail,
    loginWithToken,
    logout: loginAsGuest,
    resetPassword
  };

  return [state, actions] as [typeof state, typeof actions];
};

const Auth = createContainer(useAuth_);
Auth.Provider.displayName = "Auth.Provider";

const AuthProvider: React.FC = ({ children }) => {
  return <Auth.Provider>{children}</Auth.Provider>;
};

export const useAuth = () => {
  if (typeof window !== "undefined") {
    return Auth.useContainer();
  } else {
    return [{} as any, {} as any];
  }
};

export default AuthProvider;
